import React from 'react';
import { Download, RefreshCw, WifiOff, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { usePwa } from './pwaContext';

export function PwaBanners() {
  const {
    isOnline,
    isStandalone,
    isIos,
    canInstall,
    needReload,
    installHintVisible,
    promptInstall,
    dismissInstall,
    reloadApp
  } = usePwa();

  const showInstall =
    isOnline &&
    !needReload &&
    !isStandalone &&
    installHintVisible &&
    (canInstall || isIos);

  return (
    <div className="pointer-events-none fixed inset-x-0 bottom-[calc(4.25rem+env(safe-area-inset-bottom,0px))] z-[80] flex flex-col gap-2 p-3 md:bottom-4 md:p-4">
      {!isOnline && (
        <div
          className="pointer-events-auto mx-auto w-full max-w-xl rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-amber-900 shadow-sm"
          role="status"
          aria-live="polite"
        >
          <div className="flex items-start gap-3">
            <WifiOff className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
            <div>
              <p className="text-sm font-semibold">You&apos;re offline</p>
              <p className="mt-0.5 text-xs leading-relaxed text-amber-800/90">
                Some features may be unavailable until your connection is restored.
              </p>
            </div>
          </div>
        </div>
      )}

      {needReload && (
        <div
          className="pointer-events-auto mx-auto w-full max-w-xl rounded-xl border border-border bg-white px-4 py-3 shadow-sm"
          role="status"
          aria-live="polite"
        >
          <div className="flex items-center justify-between gap-3">
            <div>
              <p className="text-sm font-semibold text-foreground">A new version is available</p>
              <p className="mt-0.5 text-xs text-muted-foreground">
                Reload when you&apos;re ready. Your current work will not be interrupted until then.
              </p>
            </div>
            <Button type="button" size="sm" onClick={reloadApp}>
              <RefreshCw className="h-4 w-4" aria-hidden />
              Reload
            </Button>
          </div>
        </div>
      )}

      {showInstall && (
        <div
          className="pointer-events-auto mx-auto w-full max-w-xl rounded-xl border border-border bg-white px-4 py-3 shadow-sm"
          role="dialog"
          aria-label="Install CW Student"
        >
          <div className="flex items-start gap-3">
            <div className="flex-1">
              <p className="text-sm font-semibold text-foreground">Install CW Student</p>
              {isIos && !canInstall ? (
                <p className="mt-0.5 text-xs leading-relaxed text-muted-foreground">
                  On iPhone, tap the Share button, then Add to Home Screen.
                </p>
              ) : (
                <p className="mt-0.5 text-xs text-muted-foreground">
                  Install the app for a full-screen experience.
                </p>
              )}
            </div>
            <div className="flex shrink-0 items-center gap-1">
              {canInstall && (
                <Button type="button" size="sm" onClick={promptInstall}>
                  <Download className="h-4 w-4" aria-hidden />
                  Install
                </Button>
              )}
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={dismissInstall}
                aria-label="Dismiss install hint"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
